import { Card, CardContent } from '@recipes/ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { PageHeader } from '@/components/layout/page-header';
import { ThemePicture } from '@/features/appearance/theme-picture';
import { useAppearance } from '@/features/appearance/use-appearance';
import { LegalLinks } from '@/features/legal/legal-links';

export interface AuthLayoutProps {
  title: string;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  testId?: string;
}

/**
 * Centered card used by every auth screen. On wide screens the theme's picture stands beside
 * the form (docs/partial/appearance-and-themes-spec.md 5.3) – beside, never behind: text does
 * not sit on a picture.
 */
export function AuthLayout({ title, description, children, footer, testId }: AuthLayoutProps) {
  const { t } = useTranslation();
  const { theme, backgroundStyle } = useAppearance();
  return (
    <div
      className="mx-auto flex min-h-[80vh] w-full max-w-4xl items-center gap-8 p-4"
      data-testid={testId}
    >
      <div className="mx-auto flex w-full max-w-md flex-col justify-center">
        <Link to="/" className="mb-6 text-center text-xl font-bold text-primary">
          {t('app.name')}
        </Link>
        <Card>
          <CardContent className="p-6">
            <PageHeader title={title} description={description} className="mb-6" />
            {children}
          </CardContent>
        </Card>
        {footer && <div className="mt-4 text-center text-sm text-muted-foreground">{footer}</div>}
        <LegalLinks className="mt-6 justify-center" />
      </div>
      {backgroundStyle !== 'NONE' && (
        <ThemePicture
          theme={theme}
          className="hidden aspect-[4/5] w-full max-w-sm shrink rounded-2xl border lg:block"
        />
      )}
    </div>
  );
}
